class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.ELM_inputReset = this.querySelector('button[type="reset"]');
    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );
    this.isOpen = false;
    this.ELM_headerSearch = this.closest(".header__search");
    this.header = document.querySelector(".header");
    this.ELM_closeBtn = this.querySelector(".close__button");
    this.ELM_modalOverlay =
      this.ELM_headerSearch?.querySelector(".modal-overlay");

    this.clickOutsideHandler = this.onClickOutside.bind(this);

    this.setupEventListeners();

    if (this.ELM_inputReset) {
      this.ELM_inputReset.addEventListener("click", () => {
        setTimeout(() => {
          this.input.value = "";
          this.input.focus();
        }, 0);
        this.ELM_inputReset?.classList.add("hidden");
      });
    }

    if (this.ELM_closeBtn) {
      this.ELM_closeBtn.addEventListener("click", () => {
        this.close();
        this.ELM_headerSearch?.classList.remove("focusing");
        this.header
          ?.querySelector(".header__search-collapse")
          ?.classList.remove("active");
      });
    }

    if (this.ELM_modalOverlay) {
      this.ELM_modalOverlay.addEventListener("click", () => {
        this.close();
        this.ELM_headerSearch?.classList.remove("focusing");
        this.header
          ?.querySelector(".header__search-collapse")
          ?.classList.remove("active");
      });
    }
  }

  setupEventListeners() {
    const form = this.querySelector("form.search");
    form.addEventListener("submit", this.onFormSubmit.bind(this));

    this.input.addEventListener(
      "input",
      debounce((event) => {
        this.onChange(event);
        document.dispatchEvent(new CustomEvent("applyColorBadges"));
      }, 300).bind(this),
    );
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", (e) => this.onFocusOut(e));
    this.addEventListener("keyup", this.onKeyup.bind(this));
    this.addEventListener("keydown", this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.close(true);
      return;
    }
    this.ELM_inputReset?.classList.remove("hidden");
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (
      !this.getQuery().length ||
      this.querySelector('[aria-selected="true"] a')
    )
      event.preventDefault();
  }

  onFocus() {
    this.ELM_headerSearch?.classList.add("focusing");
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      this.open();
      return;
    }

    if (this.getAttribute("results") === "true") {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut(evt) {
    if (
      evt.target?.classList.contains("reset__button") ||
      evt.target?.classList.contains("search__input")
    )
      return;
    this.ELM_headerSearch?.classList.remove("focusing");

    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
      this.header
        .querySelector(".header__search-collapse")
        ?.classList.remove("active");
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === "ArrowUp" || event.code === "ArrowDown") {
      event.preventDefault();
    }
  }

  switchOption(direction) {
    if (!this.getAttribute("open")) return;

    const moveUp = direction === "up";
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll("li");
    let activeElement = this.querySelector("li");

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement =
        selectedElement.previousElementSibling ||
        allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute("aria-selected", true);
    if (selectedElement) selectedElement.setAttribute("aria-selected", false);

    this.setLiveRegionText(activeElement.textContent);
    this.input.setAttribute("aria-activedescendant", activeElement.id);
  }

  selectOption() {
    const selectedProduct = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"] button',
    );

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent("resources[type]")}=product,collection&${encodeURIComponent("resources[limit]")}=100&section_id=predictive-search`,
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, "text/html")
          .querySelector("#shopify-section-predictive-search").innerHTML;
        this.cachedResults[queryKey] = resultsMarkup;
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement || this.querySelector(".predictive-search-status");
    this.loadingText =
      this.loadingText || this.getAttribute("data-loading-text");

    this.setLiveRegionText(this.loadingText);
    this.setAttribute("loading", true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute("aria-hidden", "false");
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute("aria-hidden", "true");
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = resultsMarkup;
    }
    this.setAttribute("results", true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute("loading");
    this.setLiveRegionText(
      this.querySelector("[data-predictive-search-live-region-count-value]")
        ?.textContent,
    );
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.getElementById("shopify-section-header").getBoundingClientRect()
        .bottom;
    return this.resultsMaxHeight;
  }

  onClickOutside(event) {
    const searchContainer = this.ELM_headerSearch || this;
    if (!searchContainer.contains(event.target)) {
      this.close();
      this.ELM_headerSearch?.classList.remove("focusing");
      this.header
        ?.querySelector(".header__search-collapse")
        ?.classList.remove("active");
    }
  }

  open() {
    const resultsMaxHeight = this.getResultsMaxHeight();
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.height =
        `${this.resultsMaxHeight}px` || `${resultsMaxHeight}px`;
      this.predictiveSearchResults.style.maxHeight =
        `${this.resultsMaxHeight}px` || `${resultsMaxHeight}px`;
    }
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
    document.documentElement.classList.add("overflow-hidden");
    document.addEventListener("click", this.clickOutsideHandler);
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = "";
      this.removeAttribute("results");
      this.ELM_inputReset?.classList.add("hidden");
    }

    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute("aria-selected", false);

    this.input.setAttribute("aria-activedescendant", "");
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults?.removeAttribute("style");

    this.isOpen = false;
    document.documentElement.classList.remove("overflow-hidden");
    document.removeEventListener("click", this.clickOutsideHandler);
  }
}

customElements.define("predictive-search", PredictiveSearch);
